/* eslint max-classes-per-file: 0 */
/* eslint class-methods-use-this: 0 */

import { Notification } from '@/typings/api/Notification'
import { BaseApiParams, RegisterModule } from '@/typings/common'

import {
  registerModule as vuexRegisterModule,
  Getters,
  Mutations,
  Actions,
  Module,
  createMapper,
} from 'vuex-smart-module'
import { NotificationsApi as Api } from '@/api/resources'
import { MODULE_NAME } from './utils'


type S = NotificationState
type G = NotificationGetters
type M = NotificationMutations
type A = NotificationActions

interface NotificationListMeta {
  page?: number;
  pageSize?: number;
  count?: number;
  unread_count?: number;
}

interface GetNotificationsParams extends BaseApiParams {
  page?: number;
  pageSize?: number;
}

class NotificationState {
  public listMeta: NotificationListMeta = {};

  public unread_count = 0;

  public list: Notification[] = [];
}

class NotificationGetters extends Getters<S> {
}

class NotificationMutations extends Mutations<S> {
  public setListMeta(payload: NotificationListMeta) {
    this.state.listMeta = payload
  }

  public setCount(payload: NotificationListMeta) {
    this.state.unread_count = payload.unread_count || 0
  }

  public incCount() {
    this.state.unread_count += 1
  }

  public setList(payload: Notification[]) {
    this.state.list = payload
  }
}

class NotificationActions extends Actions<S, G, M, A> {
  private readonly api = new Api();

  public getCounts() {
    const options = { page: 1, pageSize: 5 }
    // const { meta } = await this.api.getCount(options)
    // if (meta) this.mutations.setCount({ unread_count: meta.unread_count })
    return this.actions.getNotifications(options)
  }

  public async getNotifications(params: GetNotificationsParams = {}) {
    const response = await this.api.getNotifications(params)
    const { data, meta } = response

    if (data) {
      this.mutations.setList(data)
    }

    if (meta) {
      this.mutations.setListMeta(params.pageSize ? { pageSize: params.pageSize, ...meta } : meta)
      this.mutations.setCount(meta)
    }

    return this.state.list
  }

  public async getNewNotifications(count = 0) {
    const { list } = this.state
    if (!count) return list
    const params = { pageSize: count }
    const response = await this.api.getNotifications(params)
    const { data, meta } = response

    if (data) {
      const newList = data.concat(list)
      this.mutations.setList(newList)
    }

    if (meta) {
      this.mutations.setListMeta(meta)
      this.mutations.setCount(meta)
    }

    return this.state.list
  }

  public async getMoreNotifications(options = {}) {
    const { listMeta } = this.state

    const params = {
      ...options,
      page: (listMeta.pageSize === 5) ? 1 : (listMeta.page || 0) + 1,
    }

    const response = await this.api.getNotifications(params)
    const { data, meta } = response

    if (data) {
      const map = new Map()
      this.state.list.forEach((item) => map.set(item.id, item))
      data.forEach((item) => map.set(item.id, item))
      const mergedArray = Array.from(map.values())
      this.mutations.setList(mergedArray)
    }

    if (meta) {
      this.mutations.setListMeta(meta)
      this.mutations.setCount(meta)
    }

    return this.state.list
  }

  public async readNotifications(data: { payload: Notification[] }) {
    const notificationIds = data.payload.map((_) => _.id)
    const params = { notification_ids: notificationIds }
    await this.api.readNotifications(params)

    const list = this.state.list.map((notification) => {
      const isChanged = notificationIds.includes(notification.id)
      return isChanged ? { ...notification, is_read: true } : notification
    })

    this.mutations.setList(list)
    await this.actions.getCounts()
  }

  public async readAllNotifications() {
    await this.api.readAllNotifications({})
    const list = this.state.list.map((_) => ({ ..._, is_read: true }))
    this.mutations.setList(list)
    await this.actions.getCounts()
  }
}

const storeModule = (
  new Module({
    state: NotificationState,
    getters: NotificationGetters,
    mutations: NotificationMutations,
    actions: NotificationActions,
  })
)

const mapper = createMapper(storeModule)

export {
  storeModule as notificationStoreModule,
  mapper as notificationMapper,
}

export const registerModule: RegisterModule = ({ store }): void => {
  if (registerModule.installed) return

  vuexRegisterModule(
    store,
    MODULE_NAME,
    MODULE_NAME,
    storeModule,
  )

  registerModule.installed = true
}
